/**
 * Entrar na conta.
 *
 * "Criar conta" é ação de primeira classe aqui: o cadastro é público, e conta
 * do aplicativo não depende de convite. O convite continua existindo para
 * entrar numa VIAGEM, e por isso o atalho para ele também fica visível — são
 * dois caminhos diferentes, e a tela não pode dar a entender que são um só.
 */
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAuthStore } from '../../auth/store';
import { Logo } from '../../ui/Logo';
import { Card } from '../../ui/primitives';
import { Field } from './Field';

/**
 * Depois de entrar, a Home (teste físico, item 2). A exceção é um link
 * compartilhado — atividade, dia, foto ou convite — aberto sem sessão: aí a
 * pessoa quer chegar ao que abriu, não à Home.
 */
export function destinoAposEntrar(from: string | undefined): string {
  if (from && /^\/(roteiro|memorias|convite)(\/|\?|$)/.test(from)) return from;
  return '/';
}

export function LoginScreen() {
  const navegar = useNavigate();
  const { state } = useLocation() as { state?: { from?: string } };
  const signIn = useAuthStore((s) => s.signIn);
  const erro = useAuthStore((s) => s.lastError);
  const descartadas = useAuthStore((s) => s.discardedOnSwitch);
  const dispensarAviso = useAuthStore((s) => s.dismissSwitchNotice);

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);

  async function entrar(evento: FormEvent) {
    evento.preventDefault();
    setEnviando(true);
    const ok = await signIn(email.trim(), senha);
    setEnviando(false);
    if (ok) navegar(destinoAposEntrar(state?.from), { replace: true });
  }

  return (
    <section className="auth-screen auth-screen--login">
      <EtiquetasDeMala />
      <Logo largura={98} className="auth-logo" />
      <div className="auth-login__titulo">
        <h1 className="auth-title">
          Faça suas
          <br />
          malas
        </h1>
        <p className="auth-subtitle">Entre para ver suas viagens.</p>
      </div>

      {descartadas !== null && (
        <Card variant="chip">
          <p className="auth-note" role="status">
            {descartadas === 1
              ? '1 alteração não sincronizada da conta anterior foi descartada neste aparelho.'
              : `${descartadas} alterações não sincronizadas da conta anterior foram descartadas neste aparelho.`}
          </p>
          <button type="button" className="nv-btn nv-btn--chip" onClick={dispensarAviso}>
            Entendi
          </button>
        </Card>
      )}

      <form className="auth-form" onSubmit={entrar}>
        <Field
          label="E-mail"
          type="email"
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Field
          label="Senha"
          type="password"
          autoComplete="current-password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          required
        />

        {erro && (
          <p className="auth-error" role="alert">
            {erro}
          </p>
        )}

        <button
          type="submit"
          className="nv-btn nv-btn--primary nv-btn--block"
          disabled={enviando || !email.includes('@') || senha.length === 0}
        >
          {enviando ? 'Entrando…' : 'Entrar'}
        </button>
      </form>

      <Link className="auth-link" to="/senha">
        Esqueci minha senha
      </Link>

      {/* A conta vem primeiro; o convite entra numa VIAGEM, não cria conta. As
          duas saídas ficam lado a lado, no mesmo card, para a tela não dar a
          entender que são um caminho só. */}
      <div className="auth-alt">
        <p className="auth-alt__title">Não tem conta?</p>
        <p className="auth-note">
          A conta vem primeiro. Depois você cria uma viagem ou entra numa com o
          convite que recebeu.
        </p>
        <div className="auth-alt__actions">
          <Link className="nv-btn nv-btn--tinta" to="/criar-conta">
            Criar conta
          </Link>
          <Link className="nv-btn nv-btn--secondary" to="/convite">
            Tenho um convite
          </Link>
        </div>
      </div>
    </section>
  );
}

/**
 * Três etiquetas de mala com códigos de aeroporto, feitas em código (refino
 * final): decorativas, próprias do login. Os destinos são exemplos genéricos —
 * nunca o da viagem de referência.
 */
const ETIQUETAS = [
  { codigo: 'SSA', cidade: 'Salvador', tom: 'tinta' },
  { codigo: 'LIS', cidade: 'Lisboa', tom: 'marca' },
  { codigo: 'FLN', cidade: 'Floripa', tom: 'clara' },
] as const;

function EtiquetasDeMala() {
  return (
    <div className="auth-etiquetas" aria-hidden="true">
      {ETIQUETAS.map((e) => (
        <span key={e.codigo} className={`auth-etiqueta auth-etiqueta--${e.tom}`}>
          <span className="auth-etiqueta__furo" />
          <span className="auth-etiqueta__codigo">{e.codigo}</span>
          <span className="auth-etiqueta__cidade">{e.cidade}</span>
        </span>
      ))}
    </div>
  );
}

